import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff } from 'lucide-react';
import api from '../services/api.js';
import toast from 'react-hot-toast';

export default function ResetPasswordPage() {
  const { userId, token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword]   = useState('');
  const [confirm, setConfirm]     = useState('');
  const [showPwd, setShowPwd]     = useState(false);
  const [loading, setLoading]     = useState(false);
  const [done, setDone]           = useState(false);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const strength = () => {
    // Strength = count of requirements met. 5 means all ok → save ready.
    // Colors below step from red (≤2) to orange (3–4) to green (5).
    const has = {
      len:     password.length >= 10,
      upper:   /[A-Z]/.test(password),
      lower:   /[a-z]/.test(password),
      num:     /[0-9]/.test(password),
      special: /[^A-Za-z0-9]/.test(password),
    };
    return Object.values(has).filter(Boolean).length;
  };
  const s = password ? strength() : 0;
  const strengthColor = s >= 5 ? 'var(--goal)' : s >= 3 ? 'var(--puck)' : 'var(--penalty)';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return toast.error('Passwords do not match');
    if (password.length < 10) return toast.error('Password must be at least 10 characters');
    if (password.length > 72) return toast.error('Password must be 72 characters or fewer');
    if (!/[A-Z]/.test(password)) return toast.error('Password must contain at least one uppercase letter');
    if (!/[a-z]/.test(password)) return toast.error('Password must contain at least one lowercase letter');
    if (!/[0-9]/.test(password)) return toast.error('Password must contain at least one number');
    if (!/[^A-Za-z0-9]/.test(password)) return toast.error('Password must contain at least one special character (e.g. !@#$%)');
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { userId, token, password });
      setDone(true);
      toast.success('Password reset! Please sign in.');
      setTimeout(() => { if (mountedRef.current) navigate('/login'); }, 2000);
    } catch (err) {
      const apiErr = err.response?.data?.error;
      // The backend returns "Invalid or expired reset token" for both the
      // expired-token and already-used-token cases (intentional — same shape
      // either way). Surface that with a helpful next step instead of just
      // dumping the error string.
      if (apiErr && /invalid or expired/i.test(apiErr)) {
        toast.error('This reset link has expired or has already been used. Please request a new one.', { duration: 6000 });
      } else {
        toast.error(apiErr || 'Reset failed — please try requesting a new link.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="fixed inset-0 pointer-events-none" style={{
        backgroundImage: `linear-gradient(rgba(0,212,255,0.03) 1px, transparent 1px),
                          linear-gradient(90deg, rgba(0,212,255,0.03) 1px, transparent 1px)`,
        backgroundSize: '60px 60px',
      }} />

      <div className="w-full max-w-md relative animate-slide-up">
        <div className="text-center mb-8">
          <div className="text-6xl mb-3" style={{ filter: 'drop-shadow(0 0 20px rgba(0,212,255,0.4))' }}>🏟</div>
          <h1 className="section-title text-4xl" style={{ color: 'var(--ice)' }}>Game Day</h1>
        </div>

        <div className="zen-card p-8">
          {done ? (
            <div className="text-center py-4">
              <div className="text-5xl mb-4">✅</div>
              <h2 className="text-xl font-bold mb-2">Password Reset!</h2>
              <p className="text-secondary text-sm">Redirecting you to sign in…</p>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold mb-2">Set New Password</h2>
              <p className="text-secondary text-sm mb-6">Choose a strong password for your account.</p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="label">New Password</label>
                  <div className="relative">
                    <Lock size={15} className="absolute left-3 top-3.5 text-secondary" />
                    <input
                      type={showPwd ? 'text' : 'password'}
                      className="input-field pl-9 pr-10" required
                      placeholder="At least 10 characters"
                      value={password} onChange={e => setPassword(e.target.value)}
                    />
                    <button type="button" onClick={() => setShowPwd(p => !p)}
                      className="absolute right-3 top-3.5 text-secondary hover:text-primary">
                      {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                  {/* 5-segment strength bar + requirements checklist. Bars
                      match the 5 checks done in strength() so users can see
                      which piece they still need. */}
                  {password && (
                    <div className="flex gap-1 mt-2">
                      {[1,2,3,4,5].map(i => (
                        <div key={i} className="h-1 flex-1 rounded-full transition-all duration-300"
                          style={{ background: i <= s ? strengthColor : 'var(--border)' }} />
                      ))}
                    </div>
                  )}
                  {(() => {
                    const checks = [
                      { ok: password.length >= 10,         label: 'At least 10 characters' },
                      { ok: /[A-Z]/.test(password),        label: 'One uppercase letter (A–Z)' },
                      { ok: /[a-z]/.test(password),        label: 'One lowercase letter (a–z)' },
                      { ok: /[0-9]/.test(password),        label: 'One number (0–9)' },
                      { ok: /[^A-Za-z0-9]/.test(password), label: 'One special character (e.g. !@#$%)' },
                    ];
                    return (
                      <ul style={{ marginTop: 8, paddingLeft: 0, listStyle: 'none' }}>
                        {checks.map(c => (
                          <li key={c.label}
                            style={{
                              fontSize: 12, lineHeight: 1.6,
                              color: c.ok ? 'var(--goal)' : 'var(--text-2)',
                              display: 'flex', alignItems: 'center', gap: 6,
                            }}>
                            <span style={{
                              display: 'inline-block', width: 14, textAlign: 'center',
                              color: c.ok ? 'var(--goal)' : 'var(--text-3)', fontWeight: 700,
                            }}>
                              {c.ok ? '✓' : '○'}
                            </span>
                            {c.label}
                          </li>
                        ))}
                      </ul>
                    );
                  })()}
                </div>

                <div>
                  <label className="label">Confirm Password</label>
                  <div className="relative">
                    <Lock size={15} className="absolute left-3 top-3.5 text-secondary" />
                    <input
                      type="password"
                      className="input-field pl-9" required
                      placeholder="••••••••"
                      value={confirm} onChange={e => setConfirm(e.target.value)}
                    />
                  </div>
                  {confirm && password !== confirm && (
                    <p className="text-xs mt-1" style={{ color: 'var(--penalty)' }}>Passwords don't match</p>
                  )}
                </div>

                <button type="submit" disabled={loading || password !== confirm} className="btn-ice w-full justify-center py-3">
                  {loading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : 'Reset Password'}
                </button>
              </form>

              <div className="text-center mt-4">
                <Link to="/login" className="text-secondary text-sm hover:text-ice transition-colors">
                  Back to Sign In
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
